import { DestroyRef, Injectable, inject } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BaseComponent } from "@app/components/base.component";
import { selectUserName } from "@app/store/user/selector";
import { UserState } from "@app/store/user/state";
import { Store } from "@ngrx/store";
import { tap } from "rxjs/operators";
import { UDFApiService } from "./udf-api.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable({
    providedIn: "root",
})
export class FormService extends BaseComponent {
    private user: any;
    private destroyInnerRef = inject(DestroyRef);

    constructor(
        private userStore: Store<{ userState: UserState }>,
        private udfApiSvc: UDFApiService,
    ) {
        super();
        this.subscribeToStore(this.userStore.select(selectUserName), (un) => {
            if (un) {
                this.udfApiSvc
                    .getGraphUser(un)
                    .pipe(takeUntilDestroyed(this.destroyInnerRef))
                    .subscribe((u) => {
                        this.user = u.value.map((p) => ({
                            oid: p.id,
                            displayName: p.displayName,
                            upn: p.userPrincipalName,
                        }));
                    });
            }
        });
    }

    createControls(fb: FormBuilder, formUses: any): FormGroup {
        const baseForm = fb.group({});
        formUses.forEach((field) => {
            baseForm.addControl(
                field.id,
                this.generateFormGroup(fb, baseForm, field),
            );
        });

        return baseForm;
    }

    generateFormGroup(
        fb: FormBuilder,
        baseForm: FormGroup,
        field,
        parent?,
    ): FormGroup {
        if (field.group) {
            const formGroup = fb.group({});
            field.group.forEach((item) => {
                formGroup.addControl(
                    item.id,
                    this.generateFormGroup(fb, formGroup, item, field.parent),
                );
            });
            if (field.visible === undefined)
                field.visible = field.parent.length === 0;
            return formGroup;
        } else {
            let newVal = this.bindDefaultValue(field);
            const formControl = fb.control(
                { value: newVal ? newVal : "", disabled: field.disabled },
                this.bindValidation(
                    parent.length > 0 ? [] : field.validations || [],
                ),
            );
            baseForm.addControl(field.id, formControl);

            if (field.customOptions) {
                field.customOptions.forEach((element) => {
                    const altFormControl = fb.control(
                        { value: "", disabled: false },
                        this.bindValidation(
                            parent.length > 0 ? [] : field.validations || [],
                        ),
                    );
                    baseForm.addControl(field.id + element.id, altFormControl);
                });
            }

            //this.getSelectvalues(field);
        }
        return baseForm;
    }

    bindDefaultValue(field) {
        let locval: any;

        if (field.type.type === "people-picker") {
            this.subscribeToStore(
                this.userStore.select(selectUserName).pipe(
                    tap((un) => {
                        if (un) {
                            locval = un;
                        }
                    }),
                ),
                null,
            );
        } else if (
            field.type.type === "hidden" &&
            field.disabled === true &&
            field.value !== undefined
        ) {
            locval = field.value;
        }

        return locval;
    }

    bindValidation(validations: any) {
        if (validations.length > 0) {
            const validList: any[] = [];
            validations.forEach((valid) => {
                if (valid.name == "required") {
                    validList.push(Validators.required);
                }
                if (valid.name == "pattern") {
                    validList.push(Validators.pattern(valid.validator));
                }
            });
            return Validators.compose(validList);
        }
    }

    resetFormControlValues(formUses, workflowForm) {
        formUses.forEach((field) => {
            if (field.id !== "selectIngestionDataType") {
                workflowForm.controls[field.id].reset();
            }

            field.group.forEach((item) => {
                if (item.isDefault) {
                    let formCtrl: any = workflowForm.controls[field.id];
                    if (item.type.type === "date") {
                        formCtrl.controls[item.id].setValue(new Date());
                    } else if (item.type.type === "people-picker") {
                        formCtrl.controls[item.id].setValue(
                            Object.create(this.user || null),
                        );
                    }
                }
            });
        });
    }

    watchControlChanges(formUses, workflowForm) {
        formUses.forEach((fg) => {
            fg.group
                .filter((i) => i.type.type === "custom-select" || "tagger")
                .forEach((fi) => {
                    workflowForm.controls[fg.id]
                        .get(fi.id)
                        .valueChanges.pipe(
                            takeUntilDestroyed(this.destroyInnerRef),
                        )
                        .subscribe((v) => {
                            if (fi.customOptions) {
                                fi.customOptions.forEach((element) => {
                                    if (
                                        element.value == v ||
                                        (v !== null &&
                                            v.includes(element.value))
                                    ) {
                                        workflowForm.controls[fg.id]
                                            .get(fi.id + element.id)
                                            .addValidators(
                                                this.bindValidation(
                                                    fi.validations,
                                                ),
                                            );
                                    } else {
                                        workflowForm.controls[fg.id]
                                            .get(fi.id + element.id)
                                            .clearValidators();
                                    }
                                    workflowForm.controls[fg.id]
                                        .get(fi.id + element.id)
                                        .updateValueAndValidity();
                                });
                            }
                        });
                });
        });
    }

    watchParentChanges(formUses, workflowForm, val) {
        formUses.forEach((fg) => {
            if (fg.ingestionDataTypes) {
                if (fg.ingestionDataTypes.includes(val)) {
                    fg.group.forEach((fi) => {
                        workflowForm.controls[fg.id]
                            .get(fi.id)
                            .addValidators(this.bindValidation(fi.validations));
                        workflowForm.controls[fg.id]
                            .get(fi.id)
                            .updateValueAndValidity();
                    });
                } else {
                    fg.group.forEach((fi) => {
                        workflowForm.controls[fg.id]
                            .get(fi.id)
                            .clearValidators();
                        workflowForm.controls[fg.id]
                            .get(fi.id)
                            .updateValueAndValidity();
                    });
                }
            }
        });
    }
}
